import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ChevronDown, ChevronUp, Plus, Search, Home, FileText, Folder,
  X, Loader2, Pencil, Eye, EyeOff, Trash2,
} from 'lucide-react';
import { SqsPageHeader, SqsButton } from './sqs/SqsShell';
import { toast } from 'sonner';
import { LivePreview, PreviewHeader, PreviewFooter } from './DashboardStyles';
import { SECTION_TEMPLATES } from './sqs/sectionLayouts';
import { themedConfirm, themedPrompt } from '@/lib/themed-dialog';
import MarketingToolsModal from './marketing/MarketingToolsModal';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
// Must match the editor top-bar height (DashboardContent `editor-topbar`).
const EDITOR_TOPBAR_H = 56;

// Each "page type" maps to a section-template category that drives the layout variants.
const PAGE_TYPES = [
  { id: 'blank',    label: 'Blank',         desc: 'Start from scratch',                 category: null               },
  { id: 'about',    label: 'About',         desc: 'Your story',                         category: 'introduce'        },
  { id: 'services', label: 'Services',      desc: 'Showcase what you offer',            category: 'services'         },
  { id: 'contact',  label: 'Contact',       desc: 'A simple contact form',              category: 'forms'            },
  { id: 'gallery',  label: 'Gallery',       desc: 'Image-led showcase',                 category: 'images'           },
  { id: 'portfolio',label: 'Portfolio',     desc: 'Curated work, image-first',          category: 'portfolio'        },
  { id: 'shop',     label: 'Shop',          desc: 'Products grid',                      category: 'services'         },
  { id: 'events',   label: 'Events',        desc: 'Upcoming gatherings & RSVPs',        category: 'group-events'     },
  { id: 'donate',   label: 'Donate',        desc: 'Tiered giving + impact',             category: 'donations'        },
];

export default function DashboardPages() {
  const navigate = useNavigate();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPagePicker, setShowPagePicker] = useState(false);
  const [pickerStep, setPickerStep] = useState('type');  // type | variant | name
  const [selectedType, setSelectedType] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [newTitle, setNewTitle] = useState('');
  const [creating, setCreating] = useState(false);
  const [activePageId, setActivePageId] = useState('home');
  const [activePageBlocks, setActivePageBlocks] = useState([]);
  const [homeBlockCount, setHomeBlockCount] = useState(0);
  const [site, setSite] = useState(null);
  // Raw site theme — defaults are resolved by resolveSiteTokens exactly like the editor.
  const [theme, setTheme] = useState({});
  const [openGroups, setOpenGroups] = useState({ main: true, notLinked: true, marketing: true, system: false });
  const [marketingKind, setMarketingKind] = useState(null); // 'announcement' | 'popup' | null
  const [query, setQuery] = useState('');
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  // Initial load — site + pages + home blocks
  useEffect(() => {
    Promise.all([
      axios.get(`${API}/builder/my-site`, { headers }).then(r => r.data).catch(() => null),
      axios.get(`${API}/pages`, { headers }).then(r => r.data || []).catch(() => []),
      // Home page blocks live in builder_pages (served via /api/content alias),
      // NOT in wb_pages — so /api/pages/home returns 404. Fetch /api/content
      // to actually show what's on the home page in the live preview.
      axios.get(`${API}/content`, { headers }).then(r => r.data).catch(() => null),
    ]).then(([s, p, home]) => {
      if (s) {
        setSite(s);
        if (s.theme) setTheme(t => ({ ...t, ...s.theme }));
      }
      setPages(p);
      if (home?.blocks) {
        setActivePageBlocks(home.blocks);
        setHomeBlockCount(home.blocks.length);
      }
    }).finally(() => setLoading(false));
  }, []);

  const loadPageBlocks = async (pageId) => {
    setActivePageId(pageId);
    try {
      const url = pageId === 'home' ? `${API}/content` : `${API}/pages/${pageId}`;
      const r = await axios.get(url, { headers });
      setActivePageBlocks(r.data?.blocks || []);
    } catch {
      setActivePageBlocks([]);
    }
  };

  // ── Squarespace-style "Edit" — the preview zooms up into the full-screen
  // editor. The site keeps its exact layout; only the edit chrome appears.
  const previewRef = useRef(null);
  const [zoom, setZoom] = useState(null);
  const openEditor = (pageId = activePageId) => {
    const url = `/website-builder/content${pageId && pageId !== 'home' ? `?page=${pageId}` : ''}`;
    const el = previewRef.current;
    if (!el || pageId !== activePageId || zoom) { navigate(url); return; }
    const r = el.getBoundingClientRect();
    const frame = el.querySelector('[data-testid="scaled-site-frame"]');
    const scale = parseFloat(frame?.dataset.scale || '1') || 1;
    const barH = el.querySelector('[data-testid="pages-preview-bar"]')?.offsetHeight || 0;
    const fr = frame ? frame.getBoundingClientRect() : r;
    const siteY = Math.max(0, (r.top + barH - fr.top) / scale);
    try { sessionStorage.setItem('wb:editScroll', JSON.stringify({ page: pageId, y: siteY })); } catch { /* noop */ }
    const s = 1 / scale;
    const tx = -r.left;
    const ty = EDITOR_TOPBAR_H - r.top - barH * s;
    setZoom({ rect: { top: r.top, left: r.left, width: r.width, height: r.height }, transform: `translate(${tx}px, ${ty}px) scale(${s})`, go: false });
    requestAnimationFrame(() => requestAnimationFrame(() => setZoom((z) => z && { ...z, go: true })));
    setTimeout(() => navigate(url), 520);
  };
  const zoomStyle = zoom ? {
    position: 'fixed', top: zoom.rect.top, left: zoom.rect.left, width: zoom.rect.width, height: zoom.rect.height,
    zIndex: 301, transformOrigin: '0 0', transform: zoom.go ? zoom.transform : 'none',
    transition: 'transform 480ms cubic-bezier(0.22, 1, 0.36, 1)', overflow: 'hidden',
  } : undefined;

  const filteredPages = query ? pages.filter(p => p.title.toLowerCase().includes(query.toLowerCase())) : pages;

  const createPage = async () => {
    if (!newTitle.trim()) return toast.error('Page title required');
    setCreating(true);
    try {
      const blocks = (selectedVariant?.blocks || []).map((b, i) => ({ ...b, id: 'b_' + Date.now() + '_' + i }));
      const res = await axios.post(`${API}/pages`, { title: newTitle.trim(), blocks }, { headers });
      setPages(prev => [...prev, res.data]);
      toast.success(`Created "${res.data.title}"`);
      closePicker();
      // Open the new page in the editor
      navigate('/website-builder/content');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create page');
    } finally {
      setCreating(false);
    }
  };

  const deletePage = async (page) => {
    if (!(await themedConfirm(`Delete "${page.title}"?`))) return;
    try {
      await axios.delete(`${API}/pages/${page.id}`, { headers });
      setPages(prev => prev.filter(p => p.id !== page.id));
      if (activePageId === page.id) loadPageBlocks('home');
      toast.success('Page deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const closePicker = () => {
    setShowPagePicker(false);
    setPickerStep('type');
    setSelectedType(null);
    setSelectedVariant(null);
    setNewTitle('');
  };

  // Variants for the picked page-type — fall back to a single "Blank" entry
  const variants = useMemo(() => {
    if (!selectedType) return [];
    if (!selectedType.category) {
      return [{ id: 'blank', label: 'Blank', preview: null, blocks: [] }];
    }
    return SECTION_TEMPLATES[selectedType.category] || [];
  }, [selectedType]);

  return (
    <>
      <SqsPageHeader
        breadcrumbs="Website"
        title="Pages"
        subtitle="Your site map on the left, a live preview on the right."
        actions={
          <>
            <div className="relative">
              <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search pages..."
                className="bg-white/[0.04] border border-white/[0.06] rounded-md pl-8 pr-3 py-1.5 text-[12px] text-white placeholder:text-[#64748B] w-[180px] focus:outline-none focus:border-[#8B5CF6]/40"
                data-testid="pages-search"
              />
            </div>
            <SqsButton variant="primary" icon={Plus} size="sm" onClick={() => setShowPagePicker(true)} data-testid="pages-new-btn">
              New Page
            </SqsButton>
          </>
        }
      />

      <div className="flex h-[calc(100vh-130px)]" data-testid="pages-split">
        {/* LEFT — pages sidebar (25%) */}
        <aside className="w-1/4 min-w-[320px] border-r border-white/[0.06] overflow-y-auto sqs-scrollbar">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="animate-spin text-[#A78BFA]" /></div>
          ) : (
            <div className="p-4">
              <Group id="main" label="Main Navigation" count={1 + filteredPages.length} openGroups={openGroups} setOpenGroups={setOpenGroups} onAdd={() => setShowPagePicker(true)}>
                <PageRow
                  page={{ id: 'home', title: 'Home' }}
                  icon={Home}
                  active={activePageId === 'home'}
                  blockCount={homeBlockCount}
                  systemPage
                  onSelect={() => loadPageBlocks('home')}
                  onEdit={() => openEditor('home')}
                />
                {filteredPages.map(p => (
                  <PageRow
                    key={p.id}
                    page={p}
                    icon={FileText}
                    active={activePageId === p.id}
                    blockCount={Array.isArray(p.blocks) ? p.blocks.length : undefined}
                    onSelect={() => loadPageBlocks(p.id)}
                    onEdit={() => openEditor(p.id)}
                    onDelete={() => deletePage(p)}
                  />
                ))}
              </Group>
              <Group id="notLinked" label="Not Linked"   count={0} openGroups={openGroups} setOpenGroups={setOpenGroups} onAdd={() => setShowPagePicker(true)} />
              <Group id="marketing" label="Marketing Tools" openGroups={openGroups} setOpenGroups={setOpenGroups} onAdd={() => setShowPagePicker(true)}>
                <MarketingToolRow
                  icon={FileText}
                  label="Announcement Banner"
                  description="Thin bar above your site header"
                  onClick={() => setMarketingKind('announcement')}
                  data-testid="marketing-row-announcement"
                />
                <MarketingToolRow
                  icon={Pencil}
                  label="Promotional Pop-Up"
                  description="Full pop-up shown when visitors arrive"
                  onClick={() => setMarketingKind('popup')}
                  data-testid="marketing-row-popup"
                />
              </Group>
              <Group id="system"   label="System Pages"  openGroups={openGroups} setOpenGroups={setOpenGroups} onAdd={() => setShowPagePicker(true)} />
              <div className="mt-6 pt-4 border-t border-white/[0.06] text-[10px] text-[#64748B]">
                Version 7.1 — Built on Entreprenatives
              </div>
            </div>
          )}
        </aside>

        {/* RIGHT — live preview (75%) */}
        {zoom && (
          <div className="fixed inset-0 z-[300] bg-[#0E0E23] transition-opacity duration-300" style={{ opacity: zoom.go ? 1 : 0 }} data-testid="pages-edit-zoom-backdrop">
            <div className="h-14 border-b border-white/[0.06] bg-[#0E0E23]" />
          </div>
        )}
        <main ref={previewRef} className="flex-1 bg-[#0E0E23] overflow-y-auto sqs-scrollbar" style={zoomStyle} data-testid="pages-live-preview">
          <div className="px-6 py-3 border-b border-white/[0.06] flex items-center justify-between sticky top-0 bg-[#0E0E23] z-10 transition-opacity duration-200" style={{ opacity: zoom?.go ? 0 : 1 }} data-testid="pages-preview-bar">
            <div>
              <p className="text-[10px] tracking-[0.18em] font-bold uppercase text-white/50">Live Preview</p>
              <p className="text-white text-[13px] font-bold mt-0.5" data-testid="pages-preview-title">
                {activePageId === 'home' ? 'Home' : pages.find(p => p.id === activePageId)?.title || activePageId}
                <span className="ml-2 text-[10px] tracking-wider uppercase text-[#64748B] font-medium" data-testid="pages-preview-count">
                  · {activePageBlocks.length} {activePageBlocks.length === 1 ? 'section' : 'sections'}
                </span>
              </p>
            </div>
            <SqsButton variant="secondary" size="sm" icon={Pencil} onClick={() => openEditor()} data-testid="pages-edit-active">
              Edit this page
            </SqsButton>
          </div>
          <LivePreview blocks={activePageBlocks} theme={theme} site={site} pageId="home" />
        </main>
      </div>

      {/* ── Page picker — 3 steps: pick type → pick variant → name ── */}
      {showPagePicker && (
        <div className="fixed inset-0 z-[200] bg-[#0E0E23]/95 backdrop-blur-2xl flex flex-col" data-testid="page-picker-modal">
          <div className="flex items-center justify-between px-8 py-5 border-b border-white/[0.06]">
            <button onClick={closePicker} className="text-[11px] uppercase tracking-[0.18em] text-white font-bold hover:text-[#A78BFA]" data-testid="page-picker-close">← Close</button>
            <div className="text-[11px] uppercase tracking-[0.18em] text-[#9CA3AF] font-bold">
              {pickerStep === 'type' && 'Step 1 of 3 · Page type'}
              {pickerStep === 'variant' && 'Step 2 of 3 · Pick a layout'}
              {pickerStep === 'name' && 'Step 3 of 3 · Name your page'}
            </div>
            <span className="w-16" />
          </div>
          <div className="flex-1 overflow-y-auto px-10 py-10">
            {pickerStep === 'type' && (
              <div>
                <h2 className="text-3xl font-bold text-white text-center mb-2" style={{ fontFamily: 'Syne, sans-serif' }}>What kind of page?</h2>
                <p className="text-[#9CA3AF] text-sm text-center mb-10">We'll show you 3–5 layout options once you pick a category.</p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-5 max-w-5xl mx-auto">
                  {PAGE_TYPES.map(t => (
                    <button
                      key={t.id}
                      onClick={() => {
                        setSelectedType(t);
                        if (t.category) setPickerStep('variant');
                        else { setSelectedVariant({ id: 'blank', label: 'Blank', blocks: [] }); setNewTitle(''); setPickerStep('name'); }
                      }}
                      className="group bg-[#15152E] border border-white/[0.06] rounded-xl overflow-hidden hover:border-[#8B5CF6]/40 hover:shadow-[0_0_32px_rgba(139,92,246,0.18)] transition-all text-left"
                      data-testid={`page-type-${t.id}`}
                    >
                      <div className="aspect-[16/10] bg-[#0E0E23] flex items-center justify-center">
                        <span className="text-[#A78BFA] font-bold text-lg" style={{ fontFamily: 'Syne, sans-serif' }}>{t.label}</span>
                      </div>
                      <div className="px-5 py-4">
                        <p className="text-[14px] font-bold text-white">{t.label}</p>
                        <p className="text-[11.5px] text-[#9CA3AF] mt-1">{t.desc}</p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {pickerStep === 'variant' && (
              <div>
                <button onClick={() => setPickerStep('type')} className="text-[11px] uppercase tracking-[0.18em] text-[#A78BFA] font-bold hover:text-white mb-4" data-testid="picker-back">← Back to page types</button>
                <h2 className="text-3xl font-bold text-white text-center mb-2" style={{ fontFamily: 'Syne, sans-serif' }}>Pick a layout for "{selectedType?.label}"</h2>
                <p className="text-[#9CA3AF] text-sm text-center mb-10">{variants.length} pre-built layouts you can fully customize after.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-6xl mx-auto">
                  {variants.map(v => (
                    <button
                      key={v.id}
                      onClick={() => { setSelectedVariant(v); setNewTitle(selectedType.label); setPickerStep('name'); }}
                      className="group bg-[#15152E] border border-white/[0.06] rounded-xl overflow-hidden hover:border-[#A78BFA]/50 hover:shadow-[0_0_32px_rgba(139,92,246,0.18)] transition-all text-left"
                      data-testid={`page-variant-${v.id}`}
                    >
                      <div className="aspect-[16/10] relative bg-[#0E0E23] overflow-hidden">
                        {v.preview && typeof v.preview === 'string' && v.preview.startsWith('http') ? (
                          <img src={v.preview} alt={v.label} className="absolute inset-0 w-full h-full object-cover" />
                        ) : (
                          <div className="absolute inset-0 bg-gradient-to-br from-[#1F1F3D] to-[#0E0E23]" />
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                        <span className="absolute bottom-3 left-3 text-[10px] tracking-[0.18em] font-bold uppercase text-white bg-black/40 px-2 py-1 rounded">{(v.blocks || []).length} blocks</span>
                      </div>
                      <div className="px-4 py-3">
                        <p className="text-[14px] font-bold text-white">{v.label}</p>
                        <p className="text-[11px] text-[#9CA3AF] mt-1">{(v.blocks || []).map(b => b.type).slice(0, 4).join(' · ')}</p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {pickerStep === 'name' && (
              <div className="max-w-md mx-auto pt-10">
                <button onClick={() => setPickerStep(selectedType?.category ? 'variant' : 'type')} className="text-[11px] uppercase tracking-[0.18em] text-[#A78BFA] font-bold hover:text-white mb-4" data-testid="picker-name-back">← Back</button>
                <h2 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Syne, sans-serif' }}>Name your page</h2>
                <p className="text-[#9CA3AF] text-sm mb-6">{selectedType?.label} · {selectedVariant?.label}</p>
                <input
                  autoFocus
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  placeholder="Page title"
                  className="w-full bg-white/[0.04] border border-white/[0.08] rounded-md px-4 py-3 text-[14px] text-white placeholder:text-[#64748B] focus:outline-none focus:border-[#A78BFA]/50"
                  onKeyDown={(e) => { if (e.key === 'Enter') createPage(); }}
                  data-testid="new-page-title"
                />
                <div className="flex justify-end gap-2 mt-5">
                  <SqsButton variant="ghost" size="sm" onClick={closePicker}>Cancel</SqsButton>
                  <SqsButton variant="primary" size="sm" onClick={createPage} disabled={creating} data-testid="new-page-create">
                    {creating ? 'Creating…' : 'Create Page'}
                  </SqsButton>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Iter 191x — Marketing Tools modals (Announcement + Popup) */}
      {marketingKind && (
        <MarketingToolsModal
          kind={marketingKind}
          subdomain={site?.subdomain || ''}
          onClose={() => setMarketingKind(null)}
        />
      )}
    </>
  );
}

function MarketingToolRow({ icon: Icon = FileText, label, description, onClick, ...rest }) {
  return (
    <div
      onClick={onClick}
      className="flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer hover:bg-white/[0.04] border border-transparent hover:border-white/[0.08] transition-all"
      {...rest}
    >
      <Icon size={13} className="text-[#A78BFA]" />
      <div className="flex-1 min-w-0">
        <span className="text-[12.5px] font-medium text-white/90 block truncate">{label}</span>
        <span className="text-[10px] text-[#64748B] tracking-wide">{description}</span>
      </div>
    </div>
  );
}

function Group({ id, label, count, children, openGroups, setOpenGroups, onAdd }) {
  return (
    <div className="mb-3">
      <button
        onClick={() => setOpenGroups(p => ({ ...p, [id]: !p[id] }))}
        className="w-full flex items-center justify-between px-3 py-2 text-[10px] uppercase tracking-[0.18em] text-[#9CA3AF] font-bold hover:text-white"
        data-testid={`pages-group-${id}`}
      >
        <span>{label}{count !== undefined && <span className="text-[#64748B] ml-2 normal-case tracking-normal">({count})</span>}</span>
        <div className="flex items-center gap-2">
          <span onClick={(e) => { e.stopPropagation(); onAdd && onAdd(); }} className="p-1 hover:bg-white/[0.06] rounded-md cursor-pointer" data-testid={`pages-add-${id}`}><Plus size={12} /></span>
          {openGroups[id] ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
        </div>
      </button>
      {openGroups[id] && <div className="mt-1">{children}</div>}
    </div>
  );
}

function PageRow({ page, icon: Icon = FileText, active, systemPage, blockCount, onSelect, onEdit, onDelete }) {
  return (
    <div
      onClick={onSelect}
      className={`flex items-center gap-3 px-3 py-2 rounded-lg group cursor-pointer transition-all ${active ? 'bg-[#A78BFA]/10 border border-[#A78BFA]/30' : 'hover:bg-white/[0.04] border border-transparent'}`}
      data-testid={`pages-row-${page.id}`}
    >
      <Icon size={13} className={active ? 'text-[#A78BFA]' : 'text-[#9CA3AF]'} />
      <div className="flex-1 min-w-0">
        <span className={`text-[12.5px] font-medium truncate block ${active ? 'text-white' : 'text-white/90'}`}>{page.title}</span>
        {typeof blockCount === 'number' && (
          <span className="text-[10px] text-[#64748B] tracking-wider uppercase" data-testid={`pages-row-count-${page.id}`}>
            {blockCount} {blockCount === 1 ? 'section' : 'sections'}
          </span>
        )}
      </div>
      {systemPage ? (
        <span className="text-[9px] text-[#64748B] opacity-0 group-hover:opacity-100">SYSTEM</span>
      ) : (
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100">
          <button onClick={(e) => { e.stopPropagation(); onEdit && onEdit(); }} className="p-1 text-[#9CA3AF] hover:text-white" data-testid={`pages-edit-${page.id}`}><Pencil size={11} /></button>
          {onDelete && <button onClick={(e) => { e.stopPropagation(); onDelete(); }} className="p-1 text-[#9CA3AF] hover:text-red-400" data-testid={`pages-delete-${page.id}`}><Trash2 size={11} /></button>}
        </div>
      )}
    </div>
  );
}
